import React, { FC, useState, useEffect, useRef } from 'react';
import ArrowRoundButton from '../Atoms/ArrowRoundButton';
import { DATE_DISPLAY_TYPE } from '../../constants/Training';
import moment from 'moment';

import { makeStyles } from '@material-ui/core/styles';
import { Box, Select, MenuItem, FormControl } from '@material-ui/core';

const useStyles = makeStyles({
  dateArea: {
    display: 'flex',
    justifyContent: 'space-around',
    width: '100%',
    lineHeight: '1.3em',
  },
  selectBox: {
    fontSize: '20px',
    minWidth: '80px',
    marginRight: '10px',
    '& .MuiSelect-select': {
      paddingTop: '5px',
      paddingBottom: '5px',
    },
  },
  yearSelect: {
    minWidth: '100px',
  },
});

interface DateSelectorWithPickerProps {
  date: moment.Moment;
  className: string;
  dateDisplayType: DATE_DISPLAY_TYPE;
  minusDate: (className: string) => void;
  plusDate: (className: string) => void;
  onDateChange: (newDate: moment.Moment) => void;
}

// レポート(endReport, startReport共通) + 年月選択機能付き.
export const DateSelectorWithPicker: FC<DateSelectorWithPickerProps> = ({
  date,
  className,
  dateDisplayType,
  minusDate,
  plusDate,
  onDateChange,
}) => {
  const classes = useStyles();
  const [selectedYear, setSelectedYear] = useState<number>(date.year());
  const [selectedMonth, setSelectedMonth] = useState<number>(date.month() + 1);

  // 年の選択肢を生成（現在の年から前後5年）
  const currentYear = moment().year();
  const yearOptions = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

  // 月の選択肢を生成
  const monthOptions = Array.from({ length: 12 }, (_, i) => i + 1);

  // 内部状態を追跡するフラグ
  const isInternalUpdate = useRef(false);

  // この useEffect は削除 - 年月の選択は直接 onChange ハンドラで処理する

  // 矢印ボタンでの月変更時に選択値を更新
  useEffect(() => {
    // 内部更新フラグを設定
    isInternalUpdate.current = true;
    // dateオブジェクトのクローンを作成して使用する
    // これにより、dateオブジェクト自体が変更されても正しく値を取得できる
    const currentDate = moment(date);
    const year = currentDate.year();
    const month = currentDate.month() + 1;

    // 状態を更新
    setSelectedYear(year);
    setSelectedMonth(month);

    console.log('DateSelectorWithPicker: date changed', currentDate.format('YYYY-MM'), 'year:', year, 'month:', month);
  }, [date]);

  // デバッグ用：selectedYearとselectedMonthの変更を監視
  useEffect(() => {
    console.log('DateSelectorWithPicker: selectedYear changed to', selectedYear);
  }, [selectedYear]);

  useEffect(() => {
    console.log('DateSelectorWithPicker: selectedMonth changed to', selectedMonth);
  }, [selectedMonth]);

  return (
    <>
      <Box className={classes.dateArea}>
        <ArrowRoundButton arrowDirection="left" onClick={() => minusDate(className)} />
        <Box
          display="flex"
          flexWrap="wrap"
          justifyContent="center"
          alignItems="center"
          width={dateDisplayType === DATE_DISPLAY_TYPE.ThePastYear ? '350px' : '300px'}
          height="50px"
        >
          <Box
            display="flex"
            width={{ xs: '100%', sm: '100%', md: 'auto' }}
            justifyContent="center"
            flexWrap="wrap"
            alignItems="center"
          >
            <FormControl className={`${classes.selectBox} ${classes.yearSelect}`}>
              <Select
                value={selectedYear}
                onChange={(e) => {
                  const newYear = e.target.value as number;
                  setSelectedYear(newYear);
                  // 年が変更されたら新しい日付を作成して親コンポーネントに通知
                  const newDate = moment(`${newYear}-${selectedMonth}`, 'YYYY-M');
                  if (newDate.isValid()) {
                    console.log('Year changed directly:', newDate.format('YYYY-MM'));
                    onDateChange(newDate);
                  }
                }}
                data-test="year-select"
              >
                {yearOptions.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}年
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl className={classes.selectBox}>
              <Select
                value={selectedMonth}
                onChange={(e) => {
                  const newMonth = e.target.value as number;
                  setSelectedMonth(newMonth);
                  // 月が変更されたら新しい日付を作成して親コンポーネントに通知
                  const newDate = moment(`${selectedYear}-${newMonth}`, 'YYYY-M');
                  if (newDate.isValid()) {
                    console.log('Month changed directly:', newDate.format('YYYY-MM'));
                    onDateChange(newDate);
                  }
                }}
                data-test="month-select"
              >
                {monthOptions.map((month) => (
                  <MenuItem key={month} value={month}>
                    {month}月
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {dateDisplayType === DATE_DISPLAY_TYPE.Day ? (
              <>
                {date.format('DD')}
                <Box component="span" display="inline-block" m="0 0 0 0" height="16px" fontSize="16px">
                  日
                </Box>
              </>
            ) : null}
          </Box>
          {dateDisplayType === DATE_DISPLAY_TYPE.ThePastYear ? (
            <Box
              display="flex"
              alignItems={{ xs: 'flex-start', sm: 'flex-start', md: 'center' }}
              mt={{ xs: '15px', sm: '15px', md: '0' }}
            >
              <Box component="span" display="inline-block" fontSize="16px" height="16px">
                までの1年分を表示
              </Box>
            </Box>
          ) : null}
        </Box>
        <ArrowRoundButton arrowDirection="right" onClick={() => plusDate(className)} />
      </Box>
    </>
  );
};

export default DateSelectorWithPicker;
